<template>
  <div 
    class="discovery-item"
    :class="{ 
      'missing': isMissing,
      'failed': isFailed,
      'clickable': isClickable
    }"
    @click="handleClick"
  >
    <!-- Discovery header -->
    <div class="discovery-header">
      <div class="discovery-type-info">
        <img :src="typeIcon" class="discovery-type-icon" />
        <span class="discovery-type-label">{{ typeLabel }}</span>
      </div>
      <div class="discovery-member">
        {{ discovererLabel }}: {{ discovery.member_name }}
      </div>
    </div>
    
    <!-- Discovery name and status -->
    <div class="discovery-name">
      <div v-if="isMissing || isFailed" class="status-badge" :class="{ 'failed-badge': isFailed }">
        {{ statusBadgeText }}
      </div>
      <div class="name-text">{{ discovery.discoverable_name }}</div>
      <!-- Show player's answer for Timeline -->
      <div v-if="isTimeline && discovery.input_content" class="input-content">
        回答: {{ discovery.input_content }}
      </div>
    </div>
    
    <!-- Missing/Failed message -->
    <div v-if="statusMessage" class="missing-message">
      {{ statusMessage }}
    </div>
    
    <!-- Unlockables section -->
    <div v-if="hasUnlockables" class="unlockables">
      <div class="unlockables-title">解鎖內容:</div>
      <UnlockableItem
        v-for="(unlockable, index) in discovery.unlockables"
        :key="`unlockable-${index}`"
        :unlockable="unlockable"
      />
    </div>
  </div>
</template>

<script>
import UnlockableItem from './UnlockableItem.vue';

/**
 * Discovery Item component following SOLID principles
 * Single Responsibility: Display individual discovery with its details
 * Interface Segregation: Only receives discovery data it needs
 */
export default {
  name: 'DiscoveryItem',
  
  components: {
    UnlockableItem
  },
  
  props: {
    discovery: {
      type: Object,
      required: true
    },
    isClickable: {
      type: Boolean,
      default: false
    }
  },
  
  emits: [
    'click'
  ],
  
  computed: {
    /**
     * Check if discovery is missing/failed
     */
    isMissing() {
      return Boolean(this.discovery.discoverable_missing);
    },
    
    /**
     * Check if this is a Timeline type
     */
    isTimeline() {
      return this.discovery.discoverable_type === 'Timeline';
    },

    /**
     * Check if it's a failed timeline (wrong answer)
     */
    isFailed() {
      return this.isTimeline && this.discovery.correct === false;
    },
    
    /**
     * Get appropriate discoverer label
     */
    discovererLabel() {
      if (this.isFailed) return '回答者';
      return this.isMissing ? '搜索者' : '發現者';
    },
    
    /**
     * Get status badge text
     */
    statusBadgeText() {
      return this.isFailed ? '失敗' : '未找到';
    },
    
    /**
     * Get status message for missing/failed items
     */
    statusMessage() {
      if (this.isFailed) {
        return '此時間線推理失敗，無法建立完整的事件順序。';
      }
      if (this.isMissing) {
        return '此項目已被搜尋但未能找到，可能需要更多線索或是已被移除。';
      }
      return '';
    },
    
    /**
     * Get type label
     */
    typeLabel() {
      const typeMap = {
        'Exhibit': '證物',
        'Position': '場景',
        'Related': '相關人',
        'Timeline': '時間軸',
        'Testimony': '證詞'
      };
      return typeMap[this.discovery.discoverable_type] || this.discovery.discoverable_type;
    },
    
    /**
     * Get type icon
     */
    typeIcon() {
      const iconMap = {
        'Exhibit': require('@/assets/images/exhibit-icon.png'),
        'Position': require('@/assets/images/soul-icon.png'),
        'Related': require('@/assets/images/related-icon.png'),
        'Timeline': require('@/assets/images/time-icon.png'),
        'Testimony': require('@/assets/images/related-icon.png')
      };
      return iconMap[this.discovery.discoverable_type] || '';
    },
    
    /**
     * Check if discovery has unlockables
     */
    hasUnlockables() {
      return this.discovery.unlockables && this.discovery.unlockables.length > 0;
    }
  },
  
  methods: {
    /**
     * Handle discovery item click
     */
    handleClick() {
      this.$emit('click', this.discovery);
    }
  }
};
</script>

<style lang="scss" scoped>
.discovery-item {
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  margin-bottom: 15px;
  
  &.clickable {
    cursor: pointer;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    }
  }
  
  &.missing {
    background-color: rgba(255, 200, 200, 0.8);
    border-left: 4px solid #ff6b6b;
  }
  
  &.failed {
    background-color: rgba(255, 180, 180, 0.8);
    border-left: 4px solid #ff4040;
  }
  
  .discovery-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    
    .discovery-type-info {
      display: flex;
      align-items: center;
      background-color: rgba(0, 0, 0, 0.05);
      padding: 4px 10px;
      border-radius: 15px;
      
      .discovery-type-icon {
        width: 20px;
        height: 20px;
        margin-right: 6px;
      }
      
      .discovery-type-label {
        font-size: 14px;
        color: #555;
        font-weight: 500;
      }
    }
    
    .discovery-member {
      font-size: 14px;
      color: #666;
      text-align: right;
      flex-shrink: 0;
    }
  }
  
  .discovery-name {
    margin-bottom: 10px;
    
    .status-badge {
      display: inline-block;
      font-size: 12px;
      font-weight: 600;
      background-color: #ff6b6b;
      color: white;
      padding: 2px 8px;
      border-radius: 10px;
      margin-bottom: 5px;
      
      &.failed-badge {
        background-color: #ff4040;
      }
    }
    
    .name-text {
      font-size: 18px;
      font-weight: 600;
      word-break: break-word;
      color: #333;
    }

    .input-content {
      font-size: 14px;
      color: #666;
      margin-top: 4px;
    }
  }
  
  .missing-message {
    background-color: rgba(255, 255, 255, 0.6);
    padding: 10px;
    border-radius: 6px;
    font-size: 14px;
    color: #d32f2f;
    margin-bottom: 10px;
    font-style: italic;
    line-height: 1.4;
  }
  
  .unlockables {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #ddd;
    
    .unlockables-title {
      font-weight: 600;
      margin-bottom: 8px;
      font-size: 14px;
      color: #555;
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .discovery-item {
    padding: 12px;
    
    .discovery-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
      
      .discovery-member {
        text-align: left;
      }
    }
    
    .discovery-name {
      .name-text {
        font-size: 16px;
      }
    }
    
    .missing-message {
      font-size: 13px;
    }
  }
}

// Animation for discovery items appearing
.discovery-item {
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>